import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import DataTable from "react-data-table-component";
import {
  createDeviceGroup,
  updateDeviceGroup,
  deleteDeviceGroup,
  getAllDeviceGroups,
} from "../commonApi";
import ActionCell from "../Utils/ActionCell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Loader } from "../Loader";

// Define the columns outside of the component
const getColumns = (handleEdit, handleDelete) => [
  { name: "Group Name", selector: (row) => row.groupName, sortable: true, sortField: "groupName", width: "385px" },
  { name: "Description", selector: (row) => row.description, sortable: true, sortField: "description", width: "385px" },
  {
    name: "Actions",
    cell: (row) => (
      <ActionCell row={row} onEdit={handleEdit} onDelete={handleDelete} />
    ),
    center: true,
    headerStyle: {
      justifyContent: "center", // Centers the header text
    },
  },
];

const DeviceGroup = () => {
  const [deviceGroups, setDeviceGroups] = useState([]);
  const [filteredDeviceGroups, setFilteredDeviceGroups] = useState([]);
  const [form, setForm] = useState({ groupName: "", description: "" });
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreate, setShowCreate] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0); // Changed to 0-based paging
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const expectedPageRef = useRef(0); // Added for pagination stability
  // Refs for form fields
  const groupNameRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchDataAndGroups = async () => {
      setLoading(true);
      try {
        if (!isCancelled) {
          await loadDeviceGroups();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error("Failed to load device groups");
          console.error("Error loading device groups:", error);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchDataAndGroups();
    return () => {
      isCancelled = true;
    };
  }, [page, rowsPerPage, sortField, sortOrder, search]);

  const loadDeviceGroups = async () => {
    setLoading(true);
    try {
      console.log("API Request:", { pageNo: page, pageSize: rowsPerPage, search, sortField, sortOrder });
      const response = await getAllDeviceGroups(page, rowsPerPage, sortField, sortOrder, search);
      const groups = (response?.data?.data?.content || []).filter(
        (group) => group && typeof group === "object" && group.groupName
      );
      console.log("API Response:", {
        page,
        contentLength: groups.length,
        totalElements: response?.data?.data?.totalElements,
        totalPages: response?.data?.data?.totalPages,
      });
      setDeviceGroups(groups);
      setFilteredDeviceGroups(groups); // Server-side filtering via API
      setTotalRows(response?.data?.data?.totalElements || 0);
      if (response?.data?.data?.totalElements > 0 && groups.length === 0) {
        toast.warn(`No device groups found for page ${page + 1}`);
      }
    } catch (error) {
      toast.error("Failed to load device groups");
      console.error("Error loading device groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setForm({ ...form, [name]: value });
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddClick = () => {
    setShowModal(true);
    setIsSaveDisabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let formErrors = {};
    const groupNamePattern = /^[a-zA-Z0-9\-_:]+$/;

    if (!form.groupName.trim()) {
      formErrors.groupName = "Group name is required";
    } else if (!groupNamePattern.test(form.groupName)) {
      formErrors.groupName = "Please enter valid Group Name";
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      if (formErrors.groupName) groupNameRef.current.focus();
      return;
    }

    setIsSaveDisabled(true);

    try {
      if (editing) {
        await updateDeviceGroup(editing.id, form);
        toast.success("Device group updated successfully");
      } else {
        await createDeviceGroup(form);
        toast.success("Device group created successfully");
      }
      setForm({ groupName: "", description: "" });
      setEditing(null);
      setShowModal(false);
      setPage(0); // Reset to first page
      expectedPageRef.current = 0;
      loadDeviceGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error?.message || "Failed to save device group");
    } finally {
      setIsSaveDisabled(false);
    }
  };

  const handleEdit = (id) => {
    const deviceGroup = deviceGroups.find((group) => group.id === id);
    setForm({
      groupName: deviceGroup.groupName,
      description: deviceGroup.description,
    });
    setEditing(deviceGroup);
    setShowCreate(false);
    setShowModal(true);
    setIsSaveDisabled(false);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ groupName: "", description: "" });
    setShowCreate(true);
    setIsSaveDisabled(false);
  };

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;

    setIsDeleteDisabled(true);

    try {
      await deleteDeviceGroup(deleteId);
      setDeviceGroups((prevGroups) => prevGroups.filter((group) => group.id !== deleteId));
      setFilteredDeviceGroups((prevGroups) => prevGroups.filter((group) => group.id !== deleteId));
      setPage(0); // Reset to first page
      expectedPageRef.current = 0;
      loadDeviceGroups();
      toast.success("Device group deleted successfully");
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data || "Thing is Attached to the Group, Group Cannot Be Deleted");
      } else {
        toast.error("Failed to delete device Group!");
      }
    } finally {
      setIsDeleteDisabled(false);
      setShowConfirmModal(false);
      setDeleteId(null);
    }
  };

  const columns = useMemo(() => getColumns(handleEdit, handleDelete), [handleEdit, handleDelete]);

  const handleSort = (column, sortDirection) => {
    const sortField = column.sortField || column.selector || "id";
    setSortField(sortField);
    setSortOrder(sortDirection);
    setPage(0);
    expectedPageRef.current = 0;
  };

  return (
    <div className="custom_tab_wrapper">
      <style>
        {`
          .btn_red {
            background-color: #dc3545; /* Default red */
            border-color: #dc3545;
            color: white;
          }
          .btn_red:disabled {
            background-color: #f28b94; /* Light red for Deleting... */
            border-color: #f28b94;
            color: white;
            opacity: 0.8;
          }
          .btn_light__red {
            background-color: #f8d7da; /* Light red background for Cancel */
            border-color: #f5c6cb;
            color: #721c24;
          }
          .btn_light__red:disabled {
            background-color: #f8d7da; /* Same color when disabled */
            border-color: #f5c6cb;
            color: #721c24;
            opacity: 0.65; /* Bootstrap default opacity for disabled */
          }
        `}
      </style>

      <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
        <ul className="nav nav-tabs" id="myTab">
          <li className="nav-item">
            <button
              className="nav-link active"
              id="home-tab"
              data-bs-toggle="tab"
              data-bs-target="#home"
              type="button"
              role="tab"
              aria-controls="home"
              aria-selected="true"
            >
              Device Groups: {totalRows}
            </button>
          </li>
        </ul>
        <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
          <div className="tab_content_wrapper full_height_tab">
            <div className="custom_table_action">
              <div>
                <input
                  className="form-control custom-search-input"
                  type="text"
                  onChange={handleSearchInputChange}
                  placeholder="Search by Group Name"
                  value={search}
                  style={{ width: "191px" }}
                />
              </div>
              <div>
                <Button
                  className="btn primary_btn text-center"
                  onClick={handleAddClick}
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="Add New Device Group"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </div>
            </div>
            <div className="table-responsive h-100vh-150px">
              {loading ? (
                <div className="spin-loader">
                  <Loader />
                </div>
              ) : (
                <div className="custom_table">
                  <DataTable
                    columns={columns}
                    data={filteredDeviceGroups}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    paginationDefaultPage={page + 1}
                    onChangePage={(newPage) => {
                      console.log("Changing page:", { newPage, currentPage: page, expectedPage: expectedPageRef.current });
                      if (newPage - 1 !== expectedPageRef.current) {
                        console.warn("Unexpected page reset detected:", { newPage, expectedPage: expectedPageRef.current });
                      }
                      setPage(newPage - 1);
                      expectedPageRef.current = newPage - 1;
                    }}
                    onChangeRowsPerPage={(newPerPage) => {
                      console.log("Changing rows per page:", { newPerPage, currentPage: page });
                      setRowsPerPage(newPerPage);
                      setPage(0);
                      expectedPageRef.current = 0;
                    }}
                    highlightOnHover
                    onSort={handleSort}
                    sortServer
                    fixedHeader
                    fixedHeaderScrollHeight="440px"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal for Create/Edit Device Group */}
        <Modal className="custom-modal" show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ color: "#4340d2" }}>{showCreate ? "Create Device Group" : "Edit Device Group"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formGroupName">
                <Form.Label>Group Name<span className="required-asterisk" style={{ color: "red" }}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="groupName"
                  value={form.groupName}
                  onChange={handleChange}
                  placeholder="Enter group name"
                  style={{
                    width: "100%",
                    backgroundColor: !showCreate ? "#f0f0f0" : "white",
                    color: !showCreate ? "#6c757d" : "black",
                  }}
                  isInvalid={!!errors.groupName}
                  ref={groupNameRef}
                  readOnly={!showCreate}
                />
                <Form.Control.Feedback type="invalid">{errors.groupName}</Form.Control.Feedback>
                {showCreate ? (
                  <Form.Text className="text-muted">
                    Enter a unique name containing only: letters, numbers, hyphens, colons, or underscores. A group name can't contain any spaces.
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted">Group name cannot be changed.</Form.Text>
                )}
              </Form.Group>
              <Form.Group controlId="formDescription" className="mt-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  style={{ width: "100%" }}
                  ref={descriptionRef}
                />
              </Form.Group>

              <div className="d-flex justify-content-end mt-3">
                <button type="button" className="btn light_primary_btn me-3" onClick={handleCloseModal}>
                  Cancel
                </button>
                <Button
                  className="btn primary_btn btn-loader"
                  type="submit"
                  disabled={isSaveDisabled}
                >
                  {isSaveDisabled ? "Saving..." : "Save"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div
            className="modal fade discard_modal show"
            id="delete-model"
            style={{ display: "block" }}
            aria-hidden={!showConfirmModal}
            aria-labelledby="exampleModalToggleLabel"
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <p className="modal-title" id="staticBackdropLabel">
                    Delete Device Group
                  </p>
                </div>
                <div className="modal-body">
                  <p className="modal-msg-title">Confirm Delete</p>
                  <p className="modal-msg-desc">Are you sure you want to delete this device group?</p>
                </div>
                <div className="modal-footer">
                  <Button
                    className="btn btn_red btn-loader"
                    onClick={confirmDelete}
                    disabled={isDeleteDisabled}
                  >
                    {isDeleteDisabled ? "Deleting..." : "Delete"}
                  </Button>
                  <Button
                    className="btn btn_light__red mx-2"
                    onClick={() => setShowConfirmModal(false)}
                    disabled={isDeleteDisabled}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceGroup;